import type { LeadCallRecording, LeadCallTranscript, LeadCallTranscriptSegment, TranscriptSpeaker } from '../../types/leadCall.js';

// SPEED TO LEAD STAGE 3B: the pure rules of call recording and transcripts. No I/O, no clock reads.
//
// ⚠️ A RECORDING IS STORED ONLY FOR A CALL A PERSON ACCEPTED. Outbound: the staff leg pressed a key (the
// conversation only exists after that). Inbound: `answeredAt` is set ONLY by a key press, so a recording of a call
// nobody accepted (no answer, out of hours, disabled, declined) is DISCARDED: never downloaded, never stored.
// ⚠️ Transcript text never goes in a log, an audit entry or a Slack message.

export const MEDIA_ALERT_AFTER_MS = 24 * 3_600_000;
/** A transcription event that has not arrived this long after the recording was stored is reported unavailable. */
export const TRANSCRIPT_WAIT_MS = 30 * 60_000;
export const MEDIA_LEASE_MS = 2 * 60_000;
export const SIGNED_URL_TTL_MS = 5 * 60_000;
export const DEFAULT_TRANSCRIPT_LANGUAGE = 'en-AU';

/**
 * How much of the recording came BEFORE the lead joined: lead join minus recording start, in whole seconds, never
 * negative. Recording start is Vonage's `start_time`; when that is missing or unreadable it falls back to the staff
 * leg's join time (the creator leg is what the recording is keyed to). `null` when the lead's join is unknown or a
 * needed timestamp is unreadable: playback then starts from 0, which is the safe direction (nothing is skipped).
 */
export function recordingOffsetSeconds(input: { recordingStart: unknown; staffJoinedAt: string | null; leadJoinedAt: string | null }): number | null {
  const ms = (v: unknown): number | null => {
    if (typeof v !== 'string') return null;
    const t = Date.parse(v);
    return Number.isNaN(t) ? null : t;
  };
  const lead = ms(input.leadJoinedAt);
  const start = ms(input.recordingStart) ?? ms(input.staffJoinedAt);
  if (lead === null || start === null) return null;
  return Math.max(0, Math.round((lead - start) / 1000));
}

/** Where the player should start: the offset when it is a sane positive number, else 0. */
export const playbackStartSeconds = (offset: number | null | undefined): number => (typeof offset === 'number' && Number.isFinite(offset) && offset > 0 ? offset : 0);

/** Transcript segments that start before the lead joined are the hold silence / ringback; drop them. Untimed segments stay. */
export function segmentsFromOffset<T extends { startSeconds?: number }>(segments: T[], offset: number | null | undefined): T[] {
  const from = playbackStartSeconds(offset);
  if (from === 0) return segments;
  return segments.filter((s) => typeof s.startSeconds !== 'number' || s.startSeconds >= from);
}

export const pendingRecording =(): LeadCallRecording => ({
  status: 'pending',
  vonageUrl: null,
  recordingUuid: null,
  gcsPath: null,
  sizeBytes: null,
  storedAt: null,
  deleteAfter: null,
  readyAt: null,
  attempts: 0,
  leaseUntil: null,
  lastError: null,
  alertedAt: null,
});

export const pendingTranscript = (): LeadCallTranscript => ({
  status: 'pending',
  text: null,
  segments: null,
  language: DEFAULT_TRANSCRIPT_LANGUAGE,
  at: null,
  url: null,
  reason: null,
});

/** Only Vonage's own https hosts: these URLs arrive in a webhook BODY, so they are untrusted input. */
export function isVonageFileUrl(url: unknown): url is string {
  if (typeof url !== 'string') return false;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && /(^|\.)(nexmo|vonage)\.com$/i.test(u.hostname);
  } catch {
    return false;
  }
}

/** The inbound rule: a recording is kept only when a person accepted the call (answeredAt, set by a key press). */
export function shouldStoreInboundRecording(call: { answeredAt: string | null; recordCalls: boolean }): boolean {
  return call.recordCalls === true && call.answeredAt !== null;
}

/** The outbound rule: only a call whose staff leg pressed a key ever had a conversation to record. */
export function shouldStoreOutboundRecording(call: { staffAcceptedAt: string | null; recordCalls: boolean }): boolean {
  return call.recordCalls === true && call.staffAcceptedAt !== null;
}

/** `<accountId>/<callId>.<ext>` in the private bucket; a call with no club lives under `unrouted/`. */
export function recordingObjectPath(accountId: string | null, callId: string, contentType: string): string {
  const ext = /wav/i.test(contentType) ? 'wav' : /ogg/i.test(contentType) ? 'ogg' : 'mp3';
  const safe = (v: string) => v.replace(/[^A-Za-z0-9_-]/g, '_');
  return `${accountId ? safe(accountId) : 'unrouted'}/${safe(callId)}.${ext}`;
}

/** Slack between the retention purge and the doc's own TTL, so the purge always runs first. */
export const TTL_GRACE_MS = 7 * 86_400_000;

/**
 * ⚠️ A stored recording must never outlive the doc that points at it: once the doc's TTL deletes it, the object is
 * orphaned (the purge finds recordings through the doc). So the doc's `expiresAt` is pushed to at least
 * deleteAfter + 7 days. Never shortened. `current` may be a Date, a Firestore Timestamp or null.
 */
export function expiresAtForRecording(current: unknown, deleteAfter: string): Date {
  return laterOf(current, new Date(Date.parse(deleteAfter) + TTL_GRACE_MS));
}

function laterOf(current: unknown, floor: Date): Date {
  const cur = current && typeof (current as { toDate?: unknown }).toDate === 'function' ? (current as { toDate: () => Date }).toDate() : current instanceof Date ? current : typeof current === 'string' && Number.isFinite(Date.parse(current)) ? new Date(current) : null;
  return cur && cur.getTime() >= floor.getTime() ? cur : floor;
}

export function deleteAfterIso(storedAt: Date, retentionDays: number): string {
  return new Date(storedAt.getTime() + retentionDays * 86_400_000).toISOString();
}

/** Retry backoff for a failed download: 1m, 2m, 4m ... capped at 30m. */
export function mediaBackoffMs(attempts: number): number {
  return Math.min(30 * 60_000, 60_000 * 2 ** Math.max(0, attempts - 1));
}

/**
 * Parse Vonage's transcription JSON tolerantly. ⚠️ UNVERIFIED shape (the first live transcript is the check):
 * `channels[].transcript[]` carrying a `sentence` / `raw` / `text` string. Anything else is reported as unreadable,
 * never guessed at, so the call shows "transcript unavailable" rather than a wrong transcript.
 */
export function parseTranscription(json: unknown): { text: string; segments: Array<{ channel: number; text: string; startSeconds?: number }> } | null {
  if (!json || typeof json !== 'object') return null;
  const channels = (json as { channels?: unknown }).channels;
  if (!Array.isArray(channels)) return null;
  const segments: Array<{ channel: number; text: string; startSeconds?: number }> = [];
  channels.forEach((ch, channel) => {
    const parts = (ch as { transcript?: unknown } | null)?.transcript;
    if (!Array.isArray(parts)) return;
    for (const p of parts) {
      const rec = p && typeof p === 'object' ? (p as Record<string, unknown>) : null;
      const t = rec ? (rec.sentence ?? rec.raw ?? rec.text) : typeof p === 'string' ? p : null;
      if (typeof t === 'string' && t.trim() !== '') {
        // ⚠️ UNVERIFIED shape: timing is taken ONLY from a finite non-negative number under a known key, never guessed.
        const rawStart = rec ? (rec.start_time ?? rec.start ?? rec.startTime) : undefined;
        // Vonage's documented sentence object carries `timestamp` (9630 in the docs' example, which reads as
        // MILLISECONDS; the units are not documented). ⚠️ UNVERIFIED: used only when no seconds key is present.
        const rawStamp = rec ? rec.timestamp : undefined;
        const startSeconds =
          typeof rawStart === 'number' && Number.isFinite(rawStart) && rawStart >= 0
            ? rawStart
            : typeof rawStamp === 'number' && Number.isFinite(rawStamp) && rawStamp >= 0
              ? rawStamp / 1000
              : undefined;
        segments.push({ channel, text: t.trim(), ...(startSeconds !== undefined ? { startSeconds } : {}) });
      }
    }
  });
  if (segments.length === 0) return null;
  return { text: segments.map((s) => s.text).join('\n'), segments };
}

// ---------------------------------------------------------------------------
// STAGE 4A: speaker labels from the recorded CHANNELS (never guessed by a model)
// ---------------------------------------------------------------------------

/**
 * Which person each recorded channel is. Outbound: the staff leg creates the conversation and joins first, so
 * channel 0 = staff, channel 1 = the lead. Inbound (`record` with `split: conversation`): channel 0 = the initial
 * caller, channel 1 = what the caller hears, i.e. the club. ⚠️ UNVERIFIED LIVE: Vonage does not document the
 * channel-to-participant mapping for a named conversation; the first live labelled transcript is the check, and
 * flipping it is this one table.
 */
export const CHANNEL_SPEAKERS = {
  outbound: ['staff', 'lead'],
  inbound: ['lead', 'staff'],
  /**
   * Outbound call recorded by a `record` action on the LEAD leg (STL_STEREO_RECORDING): per Vonage's sent/received
   * convention the recording leg's SENT audio is channel 0 = the lead, RECEIVED audio is channel 1 = staff. ⚠️ UNVERIFIED
   * LIVE; the media sweep logs each channel's first speech and loudness so this is checked against the join times on
   * the first live call, and flipping it is this one entry.
   */
  outboundLeadLeg: ['lead', 'staff'],
} as const satisfies Record<string, readonly [TranscriptSpeaker, TranscriptSpeaker]>;
export type SpeakerTable = keyof typeof CHANNEL_SPEAKERS;

/**
 * Label parsed segments by channel. Labels are applied ONLY when the transcription really has two channels with
 * speech (`channelsWithSpeech >= 2`); a single-channel (mono) transcript is stored as `unknown` with
 * `speakerLabels: 'unavailable'`, which the caller reports. Labelled segments are interleaved by start time when
 * every segment is timed; otherwise they keep channel order.
 */
export function labelTranscriptSegments(
  kind: 'outbound' | 'inbound',
  segments: Array<{ channel: number; text: string; startSeconds?: number }>,
  staffMemberId: string | null,
  table: SpeakerTable = kind,
): { segments: LeadCallTranscriptSegment[]; speakerLabels: 'labelled' | 'unavailable' } {
  const channelsWithSpeech = new Set(segments.map((s) => s.channel)).size;
  if (channelsWithSpeech < 2) {
    return { segments: segments.map((s) => ({ ...s, speaker: 'unknown' as const })), speakerLabels: 'unavailable' };
  }
  const map = CHANNEL_SPEAKERS[table];
  const labelled: LeadCallTranscriptSegment[] = segments.map((s) => {
    const speaker: TranscriptSpeaker = s.channel === 0 || s.channel === 1 ? map[s.channel] : 'unknown';
    return { ...s, speaker, ...(speaker === 'staff' && staffMemberId ? { staffMemberId } : {}) };
  });
  const allTimed = labelled.every((s) => typeof s.startSeconds === 'number');
  if (allTimed) labelled.sort((a, b) => (a.startSeconds as number) - (b.startSeconds as number));
  return { segments: labelled, speakerLabels: 'labelled' };
}

/** "Joe" for a known staff member (first name), "Staff" otherwise; "Lead" / "Caller" (inbound); "Speaker" when unknown. */
export function speakerLabel(speaker: TranscriptSpeaker | undefined, kind: 'outbound' | 'inbound', staffName: string | null, clubName: string | null = null): string {
  if (speaker === 'staff') {
    const raw = (staffName ?? '').trim().split(/\s+/)[0];
    if (!raw) return 'Staff';
    // As written in the team list; only a lowercase first letter ("rachel") is capitalised.
    const first = raw.charAt(0).toUpperCase() + raw.slice(1);
    return clubName && clubName.trim() ? `${first} (${clubName.trim()})` : first;
  }
  if (speaker === 'lead') return kind === 'inbound' ? 'Caller' : 'Lead';
  return 'Unclear';
}

/** The labelled transcript as plain lines ("Joe: ..."), for the model and the history view. ⚠️ Text: never logged. */
export function labelledTranscriptLines(segments: Array<{ label: string; text: string }>): string {
  return segments.map((s) => `${s.label}: ${s.text}`).join('\n');
}

// ---------------------------------------------------------------------------
// STAGE 4D: the retention SPLIT. Audio 90 days (the club's `recordingRetentionDays`); transcripts, summaries and
// outcomes 24 months. Purging the audio leaves the text; the text has its own purge date.
// ---------------------------------------------------------------------------

export const CALL_TEXT_RETENTION_DAYS = 730;
export const CALL_TEXT_RETENTION_MS = CALL_TEXT_RETENTION_DAYS * 86_400_000;

/** When a call's transcript and summary text are deleted: 730 days after the call. */
export function textDeleteAfterIso(callAtIso: string): string {
  const t = Date.parse(callAtIso);
  return new Date((Number.isNaN(t) ? 0 : t) + CALL_TEXT_RETENTION_MS).toISOString();
}

/**
 * The call doc's TTL must outlive its text: at least 730 days + the 7-day grace after the call. ⚠️ NEVER SHORTENED
 * (`current` wins when it is already later), the same rule as `expiresAtForRecording`.
 */
export function expiresAtForCallText(current: unknown, callAtIso: string): Date {
  return laterOf(current, new Date(Date.parse(callAtIso) + CALL_TEXT_RETENTION_MS + TTL_GRACE_MS));
}
